'use client';

import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { getSocket } from '@/lib/echo';
import { useEchoConnection } from '@/hooks/use-echo-connection';
import { ocrApi } from '../services/ocr.api';
import { ocrKeys } from '../services/ocr.queries';
import type { OcrResult, ExtractedDataItem } from '../types';

interface OcrProcessingEvent {
  ocr_result_id: number;
  status: string;
  current_page: number;
  total_pages: number;
  extracted: ExtractedDataItem[];
  progress_stage?: OcrResult['progress_stage'];
  error_code?: OcrResult['error_code'];
}

const FALLBACK_POLL_MS = 5_000;

/**
 * Listens to WebSocket events for real-time OCR result updates.
 * Covers both processing progress AND field-level changes (accept/update).
 *
 * The initial fetch always uses the full `getResult` endpoint so the detail
 * view has chunks/template/pages. Polling, when WebSocket is unhealthy,
 * uses the lightweight `getStatuses` endpoint and patches the cache —
 * never re-loading relations on each poll cycle.
 */
export function useOcrResultPolling(
  resultId: number,
  enabled = true
): UseQueryResult<{ data: OcrResult }> & {
  currentPage: number;
  totalPages: number;
} {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { shouldFallback } = useEchoConnection();

  useEffect(() => {
    if (!resultId || !enabled) return;

    let cancelled = false;

    getSocket().then((socket) => {
      if (cancelled) return;

      const channel = socket.private(`ocr-result.${resultId}`);

      channel.listen('.ocr-processing', (event: OcrProcessingEvent) => {
        setCurrentPage(event.current_page);
        setTotalPages(event.total_pages);

        // Update cache immediately from WebSocket event
        queryClient.setQueryData<{ data: OcrResult }>(
          ocrKeys.result(resultId),
          (old) => {
            if (!old) return old;
            const total = event.total_pages || old.data.total_pages || 0;
            const current = event.current_page ?? old.data.current_page ?? 0;
            return {
              ...old,
              data: {
                ...old.data,
                status: event.status as OcrResult['status'],
                current_page: current,
                total_pages: total,
                progress_stage:
                  event.progress_stage ?? old.data.progress_stage ?? null,
                progress_percent:
                  total > 0
                    ? Math.min(100, Math.round((current / total) * 100))
                    : 0,
                error_code: event.error_code ?? old.data.error_code ?? null,
                extracted_data:
                  event.extracted.length > 0
                    ? event.extracted
                    : old.data.extracted_data
              }
            };
          }
        );

        // Also refresh container-level queries
        queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      });

      // Refetch after subscribing in case the event was already fired
      queryClient.invalidateQueries({
        queryKey: ocrKeys.result(resultId)
      });
    });

    // Leave channel only on unmount — stay connected for field change events
    return () => {
      cancelled = true;
      getSocket().then((socket) => socket.leave(`ocr-result.${resultId}`));
    };
  }, [resultId, enabled, queryClient]);

  const query = useQuery({
    queryKey: ocrKeys.result(resultId),
    queryFn: () => ocrApi.getResult(resultId),
    enabled: !!resultId && enabled
  });

  // Lightweight fallback polling — only when WS is unhealthy and the result
  // is still in flight. Uses the batch status endpoint and patches the cache,
  // so the heavy `getResult` query isn't refired every cycle.
  const status = query.data?.data?.status;
  const shouldPoll =
    shouldFallback &&
    enabled &&
    !!resultId &&
    (status === 'pending' || status === 'processing');

  useEffect(() => {
    if (!shouldPoll) return;

    let cancelled = false;

    const pollOnce = async () => {
      try {
        const response = await ocrApi.getStatuses([resultId]);
        if (cancelled) return;
        const fresh = response.data[0];
        if (!fresh) return;

        queryClient.setQueryData<{ data: OcrResult }>(
          ocrKeys.result(resultId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              data: {
                ...old.data,
                status: fresh.status,
                current_page: fresh.current_page,
                total_pages: fresh.total_pages,
                progress_stage: fresh.progress_stage,
                progress_percent: fresh.progress_percent,
                error_code: fresh.error_code ?? old.data.error_code ?? null,
                extracted_data:
                  fresh.extracted_data && fresh.extracted_data.length > 0
                    ? fresh.extracted_data
                    : old.data.extracted_data,
                error_message: fresh.error_message ?? old.data.error_message
              }
            };
          }
        );

        if (fresh.status === 'completed') {
          // One full refetch to load chunks/pages once processing finishes.
          queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
          queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
        }
      } catch {
        // Best-effort: swallow per-poll errors.
      }
    };

    const interval = setInterval(pollOnce, FALLBACK_POLL_MS);
    void pollOnce();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [shouldPoll, resultId, queryClient]);

  return { ...query, currentPage, totalPages };
}
